/**
 * A SESSÃO VENCIDA, NUM LUGAR SÓ.
 *
 * POR QUE EXISTE. Em 10/09/2026 dois WebSockets passaram 13 h reconectando a
 * cada 10 s com tokens vencidos, e o stream de notificações chegou a 71
 * recusas por minuto — e ninguém foi mandado ao login. Não havia tratamento de
 * 401 no interceptor do axios, o WebSocket lia o token uma vez só na montagem,
 * e o stream descartava o status da resposta. Cada lugar decidia sozinho, e
 * nenhum decidia "a sessão acabou".
 *
 * Agora são duas perguntas, e as duas moram aqui:
 *   - `tokenParaConectar()`, que o WebSocket e o stream fazem antes de CADA
 *     tentativa — a primeira e cada reconexão;
 *   - `deveEncerrarPor401()`, que o interceptor do axios faz a cada 401.
 * E uma saída só, `sessaoVencida()`, que limpa a sessão e leva ao login.
 */

// O token do storage, ou `null` quando não há — ou quando o navegador bloqueia
// o storage, e aí também não há sessão para encerrar.
export function lerToken() {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

/** O `exp` do JWT, em segundos, ou `null` se o token for ilegível.
 *
 * NÃO valida assinatura, e não precisa: quem valida é o servidor. Isto só lê a
 * data, para o cliente não tentar conectar com um token que já sabe estar morto.
 */
export function expDoToken(token) {
  try {
    const parte = String(token).split('.')[1];
    const b64 = parte.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** O token está vencido?
 *
 * AUSENTE NÃO É VENCIDO, e é a regra mais importante deste arquivo. Token
 * ausente é quem não está logado: a tela de login, o visitante, o instante
 * antes de a sessão existir. Se ausente contasse como vencido, a tela de login
 * se mandaria para o login em laço, e quem abre o app seria expulso antes de
 * entrar — trocar o defeito de 10/09 por um pior. Só um token PRESENTE pode
 * estar vencido.
 *
 * Presente e ilegível conta como vencido: o servidor o recusaria de qualquer
 * jeito, e tentar conectar com ele é o laço que se quer parar.
 *
 * O relógio é o do NAVEGADOR. Um relógio adiantado encerra a sessão mais cedo,
 * pelo tamanho do adiantamento — e só tranca a entrada se o adiantamento passar
 * da validade inteira do token.
 */
export function tokenVencido(token, agoraMs = Date.now()) {
  if (!token) return false;
  const exp = expDoToken(token);
  if (exp === null) return true;
  return exp * 1000 <= agoraMs;
}

// Injetável SÓ para o teste: o jsdom não deixa trocar `window.location`, e sem
// isto não haveria como provar que o redirecionamento acontece — ou que não.
export const _navegacao = {
  caminho: () => window.location.pathname,
  ir: (url) => window.location.replace(url),
};

/** Encerra a sessão: limpa o storage e leva ao login.
 *
 * SEM TOKEN, NÃO FAZ NADA, e é isso que a deixa segura de chamar mais de uma
 * vez: o WebSocket, o stream e o axios costumam perceber a mesma sessão morta
 * juntos, o primeiro apaga o token e os demais encontram o storage vazio. É
 * também a segunda trava da regra de `tokenVencido`: nem uma chamada indevida
 * expulsa quem não tem sessão.
 */
export function sessaoVencida(origem) {
  if (!lerToken()) return;
  console.warn(`[sessao] vencida, percebida por ${origem}`);
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('account');
  } catch {
    /* com o getItem funcionando nao deveria acontecer; a navegacao abaixo
       ainda tira o usuario da tela morta */
  }
  // NAVEGAÇÃO COMPLETA, e não a do roteador: é o que garante que nenhum timer,
  // fetch ou socket da sessão morta sobreviva. Na própria tela de login não se
  // navega — lá a limpeza basta, e recarregar apagaria o que o usuário estiver
  // digitando.
  if (_navegacao.caminho() !== '/login') _navegacao.ir('/login');
}

/** O token com que se pode conectar AGORA, ou `null`.
 *
 * O WebSocket e o stream perguntam isto antes de cada tentativa — a primeira e
 * cada reconexão —, em vez de reusar o token de quando montaram. Ausente:
 * `null`, sem encerrar nada. Vencido: `null`, e a sessão é encerrada aqui.
 */
export function tokenParaConectar(origem) {
  const token = lerToken();
  if (!token) return null;
  if (tokenVencido(token)) {
    sessaoVencida(origem);
    return null;
  }
  return token;
}

/** Este erro do axios significa que a sessão acabou?
 *
 * Só um 401 que carregou o token ATUAL. Três guardas, e cada uma impede um jeito
 * de expulsar quem não devia:
 *   - rotas de `/auth/`: senha errada no login devolve 401, e não é sessão
 *     vencida — encerrar ali recarregaria a tela de quem está digitando;
 *   - requisição sem token: não é esta sessão sendo recusada. O React roda os
 *     efeitos dos filhos ANTES do efeito do AuthProvider, que é quem põe o
 *     cabeçalho no axios, então requisição sem token existe na carga. Hoje o
 *     backend responde 403 a ela, mas a regra não pode depender disso;
 *   - token diferente do atual: é uma requisição de antes de um novo login, e o
 *     401 dela não diz nada sobre a sessão que está valendo.
 */
export function deveEncerrarPor401(error) {
  if (error?.response?.status !== 401) return false;
  const url = String(error.config?.url || '');
  if (url.includes('/auth/')) return false;
  const cabecalhos = error.config?.headers;
  const bruto =
    (cabecalhos && typeof cabecalhos.get === 'function'
      ? cabecalhos.get('Authorization')
      : cabecalhos?.Authorization || cabecalhos?.authorization) || '';
  const usado = String(bruto).startsWith('Bearer ') ? String(bruto).slice(7) : null;
  if (!usado) return false;
  return usado === lerToken();
}
